import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Form, 
  Input, 
  Upload, 
  Space, 
  Alert, 
  Descriptions, 
  Tag, 
  Divider, 
  Progress, 
  message, 
  Spin,
  Result,
  Steps
} from 'antd';
import { 
  ArrowLeftOutlined, 
  CheckCircleOutlined, 
  UploadOutlined, 
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  SaveOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UploadFile } from 'antd/es/upload/interface';
import type { PlanogramSubtask } from '../../types/plan-sub-task';
import { PlanogramSubtaskService } from '../../services/planogramSubtaskService';
import StatusTag from './components/StatusTag';
import dayjs from "dayjs";

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;
const { Step } = Steps;

/**
 * Task Completion Page Component
 * Dedicated page for completing subtasks with comprehensive form and validation
 */
const TaskCompletion: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subtaskCode } = useParams<{ subtaskCode: string }>();
  
  // State management
  const [subtask, setSubtask] = useState<PlanogramSubtask | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  // Service instance
  const subtaskService = PlanogramSubtaskService.getInstance();

  // Load subtask data
  useEffect(() => {
    const loadSubtaskData = async () => {
      if (!subtaskCode) {
        setError(t('taskCompletion.taskCodeNotExists'));
        setLoading(false);
        return;
      }

      try {
        const response = await subtaskService.getSubtaskByCode(subtaskCode);
        
        if (response.success && response.data) {
          setSubtask(response.data);
          
          // Check if task can be completed
          if (response.data.status !== 'Ongoing') {
            setError(t('taskCompletion.onlyOngoingTasksCanBeCompleted'));
          }
        } else {
          setError(response.errorMessage || t('taskCompletion.loadTaskDetailsFailed'));
        }
      } catch (error) {
        console.error('Error loading subtask:', error);
        setError(t('taskCompletion.loadTaskDataFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadSubtaskData();
  }, [subtaskCode, subtaskService]);

  // Calculate overdue status
  const isOverdue = subtask ? new Date() > new Date(subtask.dueDate) : false;
  const overdueHours = subtask && isOverdue 
    ? Math.floor((new Date().getTime() - new Date(subtask.dueDate).getTime()) / (1000 * 60 * 60))
    : 0;

  // Format date
  const formatDate = (dateString: string) => {
    // return new Date(dateString).toLocaleString('zh-CN', {
    //   year: 'numeric',
    //   month: '2-digit',
    //   day: '2-digit',
    //   hour: '2-digit',
    //   minute: '2-digit'
    // });
    return dayjs(dateString).format("YYYY-MM-DD");
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      // Convert upload files to File objects
      const files = uploadFiles
        .filter(file => file.originFileObj)
        .map(file => file.originFileObj as File);

      const response = await subtaskService.completeSubtask(
        subtask.id,
        subtaskCode!,
        values.remarks,
        files
      );
      
      if (response.success && response.data) {
        setSubtask(response.data);
        setCompleted(true);
        setCurrentStep(2);
        message.success(t('taskCompletion.taskCompletedSuccessfully'));
      } else {
        message.error(response.errorMessage || t('taskCompletion.completeTaskFailed'));
      }
    } catch (error: any) {
      if (error?.errorFields) {
        message.error(t('taskCompletion.pleaseFillRequiredInfo'));
      } else {
        console.error('Error completing task:', error);
        message.error(t('taskCompletion.completeTaskFailed'));
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle file upload
  const handleUploadChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setUploadFiles(fileList);
  };

  // Custom upload request (prevent auto upload)
  const customUploadRequest = ({ onSuccess }: any) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  // File upload validation
  const beforeUpload = (file: File) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'text/plain',
      'application/zip',
      'application/x-zip-compressed'
    ];

    if (!allowedTypes.includes(file.type)) {
      message.error(t('taskCompletion.unsupportedFileFormat'));
      return false;
    }

    if (file.size > 50 * 1024 * 1024) {
      message.error(t('taskCompletion.fileSizeExceeded'));
      return false;
    }

    if (uploadFiles.length >= 10) {
      message.error(t('taskCompletion.maxFilesExceeded'));
      return false;
    }

    return true;
  };

  // Render breadcrumb navigation
  const renderBreadcrumb = () => {
    return (
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => window.history.back()}
          style={{ padding: 0, height: 'auto' }}
        >
          {t('taskCompletion.backToTaskDetail')}
        </Button>
      </div>
    );
  };

  // Render task information
  const renderTaskInfo = () => {
    if (!subtask) return null;

    return (
      <Card 
        title={t('taskCompletion.taskInfo')} 
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: 24
        }}
      >
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered>
          <Descriptions.Item label={t('taskCompletion.taskName')} span={1}>
            <Text strong>{subtask.subtaskName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('taskCompletion.taskCode') } span={2}>
            <Text code>{subtask.subtaskCode}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('taskCompletion.currentStatus')}>
            <StatusTag status={subtask.status} overdueFlag={subtask.overdueFlag} />
          </Descriptions.Item>
          <Descriptions.Item label={t('taskCompletion.parentMilestone')} span={2}>
            <Tag color="blue">{subtask.milestoneName}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('taskCompletion.assignee')}>
            <Space>
              <UserOutlined />
              <Text>{subtask.assignee}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('taskCompletion.dueDate')} span={1}>
            <Space>
              <CalendarOutlined />
              <Text type={isOverdue ? 'danger' : undefined}>
                {formatDate(subtask.dueDate)}
              </Text>
              {isOverdue && (
                <Tag color="red">{t('taskCompletion.overdueHours', { hours: overdueHours })}</Tag>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('taskCompletion.startDate')} span={1}>
            <Space>
              <ClockCircleOutlined />
              <Text>{formatDate(subtask.startDate || '')}</Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>

        {subtask.deliverables && (
          <>
            <Divider />
            <div>
              <Text strong>{t('taskCompletion.expectedDeliverables')}:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
                <Text>{subtask.deliverables}</Text>
              </div>
            </div>
          </>
        )}

        {isOverdue && (
          <>
            <Divider />
            <Alert
              message={t('taskCompletion.taskOverdue')}
              description={t('taskCompletion.taskOverdueDescription', { hours: overdueHours })}
              type="error"
              showIcon
              icon={<ExclamationCircleOutlined />}
            />
          </>
        )}
      </Card>
    );
  };

  // Render completion form
  const renderCompletionForm = () => {
    if (!subtask || completed) return null;

    return (
      <Card 
        title={t('taskCompletion.completeTask')} 
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="remarks"
            label={t('taskCompletion.remarks')}
            rules={[
              { required: true, message: t('taskCompletion.pleaseEnterRemarks') },
              { min: 3, message: t('taskCompletion.remarksMinLength') }
            ]}
          >
            <TextArea
              rows={6}
              placeholder={t('taskCompletion.remarksPlaceholder')}
              showCount
              maxLength={2000}
            />
          </Form.Item>

          {isOverdue && (
            <Form.Item
              name="overdueReason"
              label={t('taskCompletion.overdueReasonExplanation')}
              rules={[
                { required: true, message: t('taskCompletion.pleaseExplainOverdueReason') }
              ]}
            >
              <TextArea
                rows={3}
                placeholder={t('taskCompletion.overdueReasonPlaceholder')}
                showCount
                maxLength={500}
              />
            </Form.Item>
          )}

          {/* <Form.Item
            label={t('taskCompletion.uploadDeliverables')}
            extra={t('taskCompletion.uploadDeliverablesExtra')}
          >
            <Upload
              multiple
              fileList={uploadFiles}
              onChange={handleUploadChange}
              customRequest={customUploadRequest}
              beforeUpload={beforeUpload}
              onRemove={(file) => {
                setUploadFiles(prev => prev.filter(f => f.uid !== file.uid));
              }}
            >
              <Button icon={<UploadOutlined />} disabled={uploadFiles.length >= 10}>
                {t('taskCompletion.selectFiles')}
              </Button>
            </Upload>
          </Form.Item> */}

          <Divider />

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button
                type="primary"
                icon={<SendOutlined />}
                htmlType="submit"
                loading={submitLoading}
                size="large"
              >
                {t('taskCompletion.submitCompletion')}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    );
  };

  // Render completion success
  const renderCompletionSuccess = () => {
    if (!completed || !subtask) return null;

    return (
      <Result
        status="success"
        title={t('taskCompletion.taskCompletedSuccessTitle')}
        subTitle={t('taskCompletion.taskCompletedSuccessSubtitle', { taskName: subtask.subtaskName })}
        extra={[
          <Button 
            type="primary" 
            key="detail"
            onClick={() => {
               navigate(`/subtasks/${subtask.subtaskCode}`, { 
                state: { from: '/subtasks' } 
              });
            }}
          >
            {t('taskCompletion.viewTaskDetail')}
          </Button>,
          <Button 
            key="list"
            onClick={() => window.history.back()}
          >
            {t('taskCompletion.backToTaskList')}
          </Button>,
        ]}
      />
    );
  };

  // Render progress steps
  const renderProgressSteps = () => {
    const steps = [
      {
        title: t('taskCompletion.fillRemarks'),
        description: t('taskCompletion.fillRemarksDesc'),
        icon: <FileTextOutlined />
      },
      {
        title: t('taskCompletion.uploadDeliverables'),
        description: t('taskCompletion.uploadDeliverablesDesc'),
        icon: <UploadOutlined />
      },
      {
        title: t('taskCompletion.completeTask'),
        description: t('taskCompletion.completeTaskDesc'),
        icon: <CheckCircleOutlined />
      }
    ];

    return (
      <Card style={{ marginBottom: 24 }}>
        <Steps current={currentStep} items={steps} />
      </Card>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        {renderBreadcrumb()}
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>{t('taskCompletion.loadingTaskInfo')}</Text>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        {renderBreadcrumb()}
        <Result
          status="error"
          title={t('taskCompletion.loadFailed')}
          subTitle={error}
          extra={[
            <Button type="primary" onClick={() => window.location.reload()}>
              {t('taskCompletion.reload')}
            </Button>,
            <Button onClick={() => navigate('/subtasks')}>
              {t('taskCompletion.backToTaskList')}
            </Button>
          ]}
        />
      </div>
    );
  }

  // Main content
  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {renderBreadcrumb()}
      
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          {t('taskCompletion.completeTask')}
        </Title>
        <Text type="secondary">
          {t('taskCompletion.completeTaskDescription')}
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {!completed && renderProgressSteps()}
            {renderTaskInfo()}
            {renderCompletionForm()}
            {renderCompletionSuccess()}
          </Space>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t('taskCompletion.completionGuide')} style={{ position: 'sticky', top: 24 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong>{t('taskCompletion.remarksRequirements')}</Text>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>{t('taskCompletion.requirement1')}</li>
                  <li>{t('taskCompletion.requirement2')}</li>
                  <li>{t('taskCompletion.requirement3')}</li>
                  <li>{t('taskCompletion.requirement4')}</li>
                </ul>
              </div>

              <Divider />

              <div>
                <Text strong>{t('taskCompletion.deliverablesUpload')}</Text>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>{t('taskCompletion.uploadRequirement1')}</li>
                  <li>{t('taskCompletion.uploadRequirement2')}</li>
                  <li>{t('taskCompletion.uploadRequirement3')}</li>
                  <li>{t('taskCompletion.uploadRequirement4')}</li>
                </ul>
              </div>

              {isOverdue && (
                <>
                  <Divider />
                  <Alert
                    message={t('taskCompletion.overdueTask')}
                    description={t('taskCompletion.overdueTaskDescription')}
                    type="warning"
                    showIcon
                  />
                </>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TaskCompletion;